import React, { useState } from 'react'

import { SxProps, Theme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiTabs, { TabsProps as MuiTabsProps } from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

export type TabsProps = {
  centered?: boolean
  onChange?: (value: number) => void
  renderTabPanel: (settings: { value: number }) => React.ReactElement
  tabs: Array<{
    label: string
    sx?: SxProps<Theme>
    id?: string
  }>
  sx?: SxProps<Theme>
  variant?: MuiTabsProps['variant'] | 'bar'
  /** Controlled value — if provided, overrides internal state */
  value?: number
}

export function Tabs(props: TabsProps) {
  const { centered = false, onChange, renderTabPanel, tabs, sx, variant = 'standard', value: controlledValue } = props
  const [internalValue, setInternalValue] = useState(0)
  const value = controlledValue !== undefined ? controlledValue : internalValue
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setInternalValue(newValue)
    onChange?.(newValue)
  }

  return (
    <>
      <Box>
        <MuiTabs
          variant={variant === 'bar' ? 'fullWidth' : variant}
          value={value}
          onChange={handleChange}
          centered={centered}
          sx={[
            isMobile && { display: 'flex', overflowX: 'auto', whiteSpace: 'nowrap' },
            variant === 'bar' && {
              borderRadius: '6px',
              boxShadow: 1,
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          id="tabs"
        >
          {tabs.map((tab, index) => (
            <Tab
              id={tab.id}
              key={index}
              label={tab.label}
              sx={[
                isMobile && { minWidth: 'unset', padding: theme.spacing(0, 1), textTransform: 'none' },
                variant === 'bar' &&
                  index !== 0 && {
                    borderLeft: '1px solid',
                    borderLeftColor: (theme) => theme.palette.divider,
                  },
                ...(Array.isArray(tab.sx) ? tab.sx : [tab.sx]),
              ]}
            />
          ))}
        </MuiTabs>
      </Box>
      {renderTabPanel({ value })}
    </>
  )
}

export default Tabs
